/**
 * Where a dismissed card sat, so Undo puts it back in that exact slot (J-3, 2026-10-08). Undo used to
 * reload the list instead, and a reload re-ranks: the deployed run's reload came back in a different
 * order with the restored grant off page 1. The page already holds the item and both indices, so
 * nothing about its position has to be guessed.
 */
export interface DismissedSlot<T> {
    Item: T;
    ItemsIndex: number;
    RecommendedIndex: number;
}

export class DismissedSlots<T> {
    private readonly slots = new Map<string, DismissedSlot<T>>();

    constructor(private readonly keyOf: (item: T) => string) {}

    /** Records the item's slots and returns both lists without it. */
    Remove(item: T, items: T[], recommended: T[]): { Items: T[]; Recommended: T[] } {
        const key = this.keyOf(item);
        const ItemsIndex = items.findIndex((i) => this.keyOf(i) === key);
        const RecommendedIndex = recommended.findIndex((i) => this.keyOf(i) === key);
        this.slots.set(key, { Item: item, ItemsIndex, RecommendedIndex });
        return { Items: items.filter((i) => this.keyOf(i) !== key), Recommended: recommended.filter((i) => this.keyOf(i) !== key) };
    }

    /** Puts a remembered item back into the slots it left (patched by `restore`), or null when this page never removed it. */
    Restore(key: string, items: T[], recommended: T[], restore: (item: T) => T): { Items: T[]; Recommended: T[] } | null {
        const slot = this.slots.get(key);
        if (!slot) return null;
        this.slots.delete(key);
        const item = restore(slot.Item);
        const put = (list: T[], at: number): T[] => (at < 0 || list.some((i) => this.keyOf(i) === key) ? list : [...list.slice(0, at), item, ...list.slice(at)]);
        return { Items: put(items, slot.ItemsIndex), Recommended: put(recommended, slot.RecommendedIndex) };
    }

    /** A new result set invalidates every remembered slot. */
    Clear(): void {
        this.slots.clear();
    }
}
